import {useState} from 'react';

import {uniq} from 'ramda';

import {Nullish} from 'shared/web';

import {getCrossedUrls} from '../utils/getCrossedUrls';
import {getGalleryImages} from '../utils/getGalleryImages';

/** Photos further from the current one are not mounted, so a long gallery does not load at once. */
const MOUNTED_SLIDE_DISTANCE = 1;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Without the slide animation the track jumps, so it crosses no photos. */
const isMotionReduced = () =>
  typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches;

/**
 * State of the listing gallery: which photo is shown, which photos failed to load and which ones
 * the slide track is crossing. The active photo is kept by URL, so a photo failing to load does
 * not shift the selection.
 */
export function useListingGallery(pictureUrls: string[], thumbnailUrl: string | Nullish) {
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  // Photos the track slides across stay mounted until the slide ends, so it never shows an empty
  // slide - also when a second move starts while the first one is still running.
  const [crossedUrls, setCrossedUrls] = useState<string[]>([]);
  // Wrapping from the last photo to the first, or back, jumps without sliding across all photos.
  const [isWrapping, setIsWrapping] = useState(false);
  const [failedUrls, setFailedUrls] = useState<string[]>([]);

  const images = getGalleryImages(pictureUrls, thumbnailUrl, failedUrls);
  const imageCount = images.length;
  const currentIndex = Math.max(images.indexOf(activeUrl ?? ''), 0);

  const isMounted = (url: string, index: number) =>
    Math.abs(index - currentIndex) <= MOUNTED_SLIDE_DISTANCE || crossedUrls.includes(url);

  const select = (url: string, isWrap = false) => {
    const isJump = isWrap || isMotionReduced();
    const crossed = getCrossedUrls(images, currentIndex, images.indexOf(url));

    setCrossedUrls((previous) => (isJump ? [] : uniq([...previous, ...crossed])));
    setIsWrapping(isWrap);
    setActiveUrl(url);
  };

  const showAt = (index: number) =>
    select(images[(index + imageCount) % imageCount], index < 0 || index >= imageCount);

  const markFailed = (url: string) => {
    // The shown photo failed: stay at its place, i.e. move on to the photo after it.
    if (url === images[currentIndex]) {
      setActiveUrl(images[currentIndex + 1] ?? images[currentIndex - 1] ?? null);
    }
    setFailedUrls((previous) => (previous.includes(url) ? previous : [...previous, url]));
  };

  const endSlide = () => setCrossedUrls([]);

  return {images, currentIndex, isWrapping, isMounted, select, showAt, markFailed, endSlide};
}
